<template>
    <div id="outer-fragebogen">
        <div id="title-row" class="m-3">
            <h4>Anfallstyp hinzufügen</h4>
        </div>
        <div id="ilae-info">
            <b-alert show variant="info" style="font-size: small; text-align: center"><p>Weiterführende
                Informationen</p>
                <b-button variant="outline-info" v-on:click="openILAE">ILAE Klassifikation</b-button>
            </b-alert>
        </div>
        <SchlagwoerterComponent id="schlagwoerter-fragebogen" :rowsUsed="rowsUsed"
                                :activeRow="activeRow"></SchlagwoerterComponent>
        <div id="questions-fragebogen">
            <div v-if="activeRow">
                <div>
                    <h4>{{ activeRow["Frage"] }}</h4>
                    <div style="background-color: lightgray"><span v-html="activeRow['Fragen_Info_Text']"></span></div>
                    <div class="answerBox">
                        <b-form-radio stacked
                                      v-for="row in possibleNextRows"
                                      v-model="currentlySelectedNextRow"
                                      :key="row['IDX']"
                                      :value="row"
                                      @change="resetAnswerState">
                            {{ row["Antwort"] }}
                        </b-form-radio>
                        <b-form-invalid-feedback :state="answerSelected">{{
                            infoTextNoOptionSelected
                            }}
                        </b-form-invalid-feedback>
                    </div>
                </div>
            </div>
            <div v-else-if="finishRow['Anfallstyp']!==''" style="align-content: center">
                <h4>Der von Ihnen beschriebene Anfall ist: </h4>
                <h4><b>{{ finishRow["Anfallstyp"] }}</b>
                    ({{ finishRow["Anfallstyp ausführlich"] }})</h4>
                <b-form-group label-cols-sm="4" label-cols-lg="3" label="Erstmaliges Auftreten:" label-for="date-tu"
                              style="padding-top: 50px">
                    <b-form-input type="date" v-model="anfallDate"
                                  required :state="anfallDateEntered" id="date-tu"
                                  aria-describedby="input-live-feedback" :min="patient.birthDate" :max="todayDate"
                                  @change="resetDateState"/>
                    <b-form-invalid-feedback id="input-live-feedback">
                        {{ infoTextNotValidDate }}
                    </b-form-invalid-feedback>
                    <div style="margin-top: 10px; padding-top: 10px; padding-left: 5px">Bitte geben Sie Januar an, wenn
                        der Monat bzw.
                        den 1., wenn der Tag nicht bekannt ist.
                    </div>
                </b-form-group>

            </div>
            <div v-else>

            </div>
        </div>
        <div id="verwerfen-button">
            <b-button variant="outline-danger" v-on:click="showDeletionModal">Verwerfen
                <b-icon-trash></b-icon-trash>
            </b-button>
        </div>
        <div id="zurueck-button" v-if="rowsUsed.length >1">
            <b-button variant="outline-primary" v-on:click="vorherigeFrage">
                <b-icon-arrow-left-circle/>
                Zurück
            </b-button>
        </div>
        <div id="weiter-buttons">
            <b-button v-if="activeRow" variant="outline-primary" v-on:click="naechsteFrage">
                Weiter
                <b-icon-arrow-right-circle/>
            </b-button>
            <b-button v-else variant="outline-success" v-on:click="finished">Anfallstyp bestätigen
                <b-icon-check-circle/>
            </b-button>
        </div>
        <DeletionModal centered
                       :show="deletionModalDisplayed"
                       :header-message="'Anfallstyperfassung wirklich abbrechen?'"
                       :subheader-message="'Bisher gemachte Eintragungen werden nicht gespeichert.'"
                       :abort-button-label="'Erfassung fortsetzen'"
                       :confirm-button-label="'Abbrechen und Daten verwerfen'"
                       @close="closeDeletionModal"
                       @deleteConfirm="deleteAnfall"/>
        <NoDiagnoseFoundModal centered
                              :show="noAnfallTypModalDisplayed"
                              :header-message="' Anfallstypstellung nicht möglich.'"
                              :subheader-message="'Bisher gemachte Eintragungen werden nicht gespeichert.'"
                              @noDiagnoseFoundModalConfirm="deleteAnfall"/>
    </div>
</template>

<script>
import anfaelleAlgorithmModule from "../../../../js/clinical-algorithm/anfaelle/anfaelleAlgorithm";
import anfaelleAlgorithm from "../../../../js/clinical-algorithm/anfaelle/Stage1_Anfaelle.csv";
import SchlagwoerterComponent from "./SchlagwoerterComponent";
import DeletionModal from "../../../modal/DeletionModal";
import NoDiagnoseFoundModal from "../../../modal/NoDiagnoseFoundModal";
import {isoDateFormat} from "../../../../js/util";
import infoTexte from "../../../../js/clinical-algorithm/infoTexte/infoTexte";
import {mapGetters, mapState} from "vuex";

export default {
    name: "FragebogenRoot",
    components: {SchlagwoerterComponent, DeletionModal, NoDiagnoseFoundModal},
    emits: {
        canceled: null,
        finished: null
    },
    props: {
        indexOfEditedAnfall: Number
    },
    data() {
        return {
            activeRow: undefined,
            possibleNextRows: undefined,
            rowsUsed: [],
            currentlySelectedNextRow: undefined,

            finishRow: undefined,
            anfallDate: undefined,
            answerTableFields: [
                {
                    key: 'fragentext',
                    label: 'Frage'
                },
                {
                    key: 'givenAnswer',
                    label: 'Antwort'
                }
            ],
            deletionModalDisplayed: false,
            noAnfallTypModalDisplayed: false,
            infoTextNotValidDate: "",
            editingAnfall: false,
            answerSelected: null,
            infoTextNoOptionSelected: infoTexte.noOptionSelected,
            anfallDateEntered: null
        }
    },
    computed: {
        todayDate() {
            return isoDateFormat(new Date())
        },
        ...mapState('cdss/anfallsanamnese', ['anfaelle']),
        ...mapGetters('cdss', [
            'patient'
        ])
    },
    created() {
        this.activeRow = anfaelleAlgorithmModule.firstRowSupplier(anfaelleAlgorithm)
        this.rowsUsed.push(this.activeRow)
        this.executeInference()
    },
    mounted() {
        // If we edit an existing entry, re-fill with the previous answers
        if (this.indexOfEditedAnfall !== undefined) {
            console.log("Editing Anfall")
            this.rowsUsed = this.anfaelle[this.indexOfEditedAnfall].rowsUsed.map(a => {
                return {...a}
            })
            console.log("Trying to get activeRow")
            this.activeRow = {...this.anfaelle[this.indexOfEditedAnfall].rowsUsed[this.anfaelle[this.indexOfEditedAnfall].rowsUsed.length - 1]}
            //this.currentlySelectedNextRow = this.anfaelle[this.indexOfEditedAnfall].rowsUsed[1]
            this.executeInference()
            this.anfallDate = isoDateFormat(new Date(this.anfaelle[this.indexOfEditedAnfall].datum))
            this.editingAnfall = true
        }
    },
    methods: {
        executeInference() {
            console.log('execute inference, active row:', this.activeRow)
            this.possibleNextRows = anfaelleAlgorithmModule.providePossibleNextRows(this.activeRow, anfaelleAlgorithm)
            console.log('execute inference, possible next rows:', this.possibleNextRows)
            if (this.possibleNextRows.length === 0) {
                this.finishRow = this.activeRow
                this.activeRow = undefined
                if (this.finishRow["Anfallstyp"] === "") {
                    this.noAnfallTypModalDisplayed = true
                }
            }
        },
        vorherigeFrage() {
            if (this.finishRow) {
                this.anfallDateEntered = null
            }
            // Remove the last used row from the history and Preselect the previously selected row
            this.currentlySelectedNextRow = this.rowsUsed.pop()

            // Set the active row to the previous active row
            this.activeRow = this.rowsUsed[this.rowsUsed.length - 1]

            // Find the possible next rows for the given row
            this.executeInference()
        },
        naechsteFrage() {
            if (this.currentlySelectedNextRow) {
                this.answerSelected = null
                // 01: Add the new row to the list of all rows used
                this.rowsUsed.push(this.currentlySelectedNextRow)
                this.activeRow = this.currentlySelectedNextRow
                this.currentlySelectedNextRow = undefined
                // 02: Infer the next step
                this.executeInference()
            } else {
                this.answerSelected = false
            }
        },
        finished() {
            if (this.validDate(this.anfallDate)) {
                let cdssAnfall = {
                    rowsUsed: this.rowsUsed,
                    datum: new Date(this.anfallDate)
                }
                this.$emit('finished', cdssAnfall)
            }
        },
        closeDeletionModal() {
            this.deletionModalDisplayed = false;
        },
        deleteAnfall() {
            console.log('delete anfall called CANCELED EMMITTED')
            this.$emit('canceled')
            this.closeDeletionModal()
        },
        showDeletionModal() {
            this.deletionModalDisplayed = true;
        },
        openILAE() {
            open("https://www.ilae.org/files/ilaeGuideline/Classification-Scheffer-2017-GERMAN.pdf", "_blank")
        },
        validDate(date) {
            const pattern = /^\d{4}-\d{2}-\d{2}$/;
            if (!pattern.test(date)) {
                this.anfallDateEntered = false
                this.infoTextNotValidDate = infoTexte.notValidDate
                return false
            } else if (new Date(date) > new Date()) {
                this.anfallDateEntered = false
                this.infoTextNotValidDate = infoTexte.dateInTheFuture
                return false
            } else if (new Date(date) < new Date(this.patient.birthDate)) {
                this.anfallDateEntered = false
                this.infoTextNotValidDate = infoTexte.dateBeforeBirth
                return false
            } else {
                this.anfallDateEntered = null
                return true
            }
        },
        resetAnswerState(answer) {
            if (!this.answerSelected && answer !== undefined) {
                this.answerSelected = null
            }
        },
        resetDateState(date) {
            if (!this.anfallDateEntered && date !== "") {
                console.log("reset Date State")
                this.anfallDateEntered = null
            }
        }
    }
}
</script>

<style scoped>
#outer-fragebogen {
    min-height: 100%;
    width: 100%;
    display: grid;
    grid-template-areas:
      "title title title title title"
      "schlagwoerter schlagwoerter schlagwoerter schlagwoerter schlagwoerter"
      "ilae . questions . ."
      "verwerfen-button . zurueck-button weiter-buttons .";
    grid-template-columns: 1fr 150px 900px 300px 1fr;
    grid-template-rows: 40px auto 1fr auto;
    grid-column-gap: 20px;
}

#title-row {
    grid-area: title;
}

#ilae-info {
    grid-area: ilae;
    justify-self: center;
    align-self: center;
}

#schlagwoerter-fragebogen {
    grid-area: schlagwoerter;
    margin-top: 20px;
    margin-bottom: 25px;
}

#questions-fragebogen {
    max-width: 1000px;
    justify-self: left;
    align-self: center;
    grid-area: questions
}

#verwerfen-button {
    grid-area: verwerfen-button;
    padding-bottom: 5px;
    padding-left: 5px;
}

#zurueck-button {
    grid-area: zurueck-button;
    justify-self: end;
    padding-bottom: 5px;
}

#weiter-buttons {
    grid-area: weiter-buttons;
    padding-bottom: 5px;
}

.answerBox {
    margin-top: 30px;
}
</style>
